import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import Icon from '../../../components/shared/Icon';
import ConfirmDenyDialog from '../../../components/shared/ModalNew/ConfirmDenyDialog';
import ForumMemberPreview from './ForumMemberPreview';
import { Skeleton } from '../../../components/shared/Skeleton';

import { useModal } from '../../../context/modal-context';
import { usePermission } from '../../../hooks/usePermission';
import { PermissionsEnum } from '../../../types';
import { showToast } from '../../../utils';

import ForumMembersStore from '../services/forumMembers.store';
import UserStatusToggleSwitch from './UserStatusToggleSwitch';
const forumMembersStore = new ForumMembersStore();

const ForumMembersList = ({
  forumMembers,
  showToggle,
}: {
  forumMembers: any;
  showToggle?: boolean;
}) => {
  const { t } = useTranslation();

  // Permissions
  const canDeleteUser = usePermission([PermissionsEnum.UserDelete]);
  const canEditUser = usePermission([PermissionsEnum.UserEdit]);
  const canViewUserData = usePermission([PermissionsEnum.UserData]);
  const canSendActivationEmail = usePermission([
    PermissionsEnum.UserActivationEmail,
  ]);

  const { openModal, closeModal } = useModal();

  const [isSendingActEmail, setIsSendingActEmail] = useState<number | false>(
    false
  );

  const handleOpenModal = (e: any, forumMember: any) => {
    if (e.target.closest('.toggle-switch')) {
      e.stopPropagation();
      return;
    }
    e.stopPropagation();
    if (!canViewUserData) return;
    openModal(
      <ForumMemberPreview forumMember={forumMember} />,
      t('forum_members.title_forum_member_preview')
    );
  };

  const deleteForumMember = async (id: number) => {
    if (!canDeleteUser) return;
    const { success, message } = await forumMembersStore.deleteForumMember(id);
    showToast(message, success);
  };

  const handleOpenDeleteForumMemberModal = (e: any, id: number) => {
    if (!canDeleteUser) return;
    e.stopPropagation();
    openModal(
      <ConfirmDenyDialog
        infoMessage={t('forum_members.prompt_forum_member_delete')}
        onConfirm={() => deleteForumMember(id)}
        onDeny={closeModal}
        closeModal={closeModal}
      />,
      t('forum_members.title_forum_member_delete_preview')
    );
  };

  const handleResendEmail = async (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    e.stopPropagation();
    setIsSendingActEmail(id);

    showToast(t('state.submitting'), true);

    const { success, message } = await forumMembersStore.sendActivationEmail({
      id: id,
    });

    showToast(message, success);
    setIsSendingActEmail(false);
  };

  if (!forumMembers) return <Skeleton />;

  return (
    <>
      <ul className="flex flex-col gap-3">
        {forumMembers?.map((member: any) => {
          return (
            <li
              onClick={(e: any) => handleOpenModal(e, member)}
              id={member.id}
              key={member.id}
              className={`pks-card-bg  overflow-hidden rounded-lg ${
                canViewUserData
                  ? 'group pks-card-bg-hover hover:cursor-pointer'
                  : ''
              }`}
            >
              <div className="flex gap-4 items-center justify-between ml-[6px] pl-[22px] pr-4 py-3 bg-white">
                <div className="pks-layout-col group">
                  {showToggle && (
                    <div className="-mb-2">
                      <UserStatusToggleSwitch id={Number(member.id)} />
                    </div>
                  )}
                  <p className="text-lg font-bold">{member?.name}</p>
                  {member?.organization?.name && (
                    <p className="text-secondary-400">
                      {member?.organization?.name}
                    </p>
                  )}
                  <p className="text-secondary-400">{member?.email}</p>
                </div>
                <div className="inline-flex items-center flex-col lg:flex-row gap-2">
                  {canSendActivationEmail && member?.status === 2 && (
                    <>
                      <Tooltip id="resend-email-tooltip" place="top" noArrow />

                      <button
                        type="button"
                        aria-label={t('forum_members.resend_activation_email')}
                        className={`block lg:mt-1 p-2  ${
                          isSendingActEmail === member.id
                            ? 'cursor-default text-gray-500'
                            : 'hover:text-primary-300'
                        }`}
                        data-tooltip-id="resend-email-tooltip"
                        data-tooltip-variant="light"
                        data-tooltip-content={t(
                          'forum_members.resend_activation_email'
                        )}
                        onClick={(e) => handleResendEmail(e, member.id)}
                        disabled={
                          isSendingActEmail === member.id ? true : false
                        }
                      >
                        <Icon name="resendEmail" />
                      </button>
                    </>
                  )}
                  {canEditUser && canViewUserData && (
                    <Link
                      to={`${member.id}`}
                      className="block transform scale-125 p-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Icon
                        name="pencil"
                        aria-hidden="true"
                        className="hover:text-primary-300"
                        tabIndex={-1}
                      />
                    </Link>
                  )}
                  {canDeleteUser && (
                    <button
                      aria-label={t('forum_members.delete_member')}
                      className="block p-2"
                      onClick={(e) =>
                        handleOpenDeleteForumMemberModal(e, member.id)
                      }
                    >
                      <Icon
                        name="trash"
                        aria-hidden="true"
                        className="text-white"
                        tabIndex={-1}
                      />
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default ForumMembersList;
